/**
 * Fullscreen Screen Navigation
 *
 * Manages smooth transitions between fullscreen screens using GSAP.
 * The action bar markup exists once in the template; it is cloned into every
 * screen so it scrolls with each screen's content instead of sitting fixed on
 * top of it. Anchor links (cards + back links) crossfade between screens.
 */

import gsap from '../../../source/vendor/gsap/esm/all.js';

export function initScreenNavigation() {
    const fullscreen = document.querySelector('.fullscreen');
    if (!fullscreen) {
        return;
    }

    const screens = fullscreen.querySelectorAll('.screen');
    if (!screens.length) {
        return;
    }

    // Clone the single action bar into each screen, then remove the original so
    // the bar is part of each screen's flow and never overlaps the content.
    const sourceActions = fullscreen.querySelector('.fullscreen-actions');
    if (sourceActions) {
        screens.forEach((screen, index) => {
            const actions = sourceActions.cloneNode(true);

            // The back link is only relevant once the visitor has left the
            // intro screen, so hide it on the first screen's bar.
            const back = actions.querySelector('.fullscreen-back');
            if (back) {
                back.style.display = index === 0 ? 'none' : 'block';
            }

            screen.insertBefore(actions, screen.firstChild);
        });
        sourceActions.remove();
    }

    let currentScreen = screens[0];
    const screenMap = {};

    // Build a map of screen IDs to elements
    screens.forEach((screen) => {
        if (screen.id) {
            screenMap[screen.id] = screen;
        }
    });

    // Set initial state - only first screen visible
    gsap.set(screens, { opacity: 0, pointerEvents: 'none', zIndex: 1 });
    if (currentScreen) {
        gsap.set(currentScreen, { opacity: 1, pointerEvents: 'auto', zIndex: 10 });
    }

    // Inactive screens are only hidden visually (opacity + pointer-events), which
    // does NOT remove their links/buttons from the keyboard tab order or the
    // accessibility tree. Marking them `inert` keeps keyboard focus and screen
    // readers on the visible screen, so users don't tab through invisible screens
    // before reaching the footer.
    screens.forEach((screen) => {
        if (screen !== currentScreen) {
            screen.setAttribute('inert', '');
        }
    });
    if (currentScreen) {
        currentScreen.removeAttribute('inert');
    }

    // Handle close buttons (one per cloned bar) - back or home redirect
    fullscreen.querySelectorAll('.fullscreen-close-btn').forEach((closeButton) => {
        closeButton.addEventListener('click', (e) => {
            e.preventDefault();
            if (history.length > 1) {
                history.back();
            } else {
                window.location.href = closeButton.getAttribute('data-home-url') || '/';
            }
        });
    });

    // Handle anchor links to screens
    const screenLinks = fullscreen.querySelectorAll('a[href^="#"]');
    screenLinks.forEach((link) => {
        link.addEventListener('click', function (e) {
            const targetId = this.getAttribute('href').substring(1);
            const targetScreen = screenMap[targetId];

            if (targetScreen && targetScreen !== currentScreen) {
                e.preventDefault();
                navigateToScreen(targetScreen);
            }
        });
    });

    function navigateToScreen(targetScreen) {
        // Capture the outgoing screen in a stable reference: `currentScreen` is
        // reassigned before the fade-out's async onComplete fires.
        const outgoingScreen = currentScreen;
        // Un-inert the incoming screen first so its controls can receive focus.
        targetScreen.removeAttribute('inert');

        // Move keyboard focus into the incoming screen BEFORE the outgoing one
        // is hidden. When the previously focused control becomes `inert`, the
        // browser would otherwise drop focus to the next focusable element in
        // the document (the footer). Where focus lands is deliberate:
        //  - Entering a sub-screen: the Back arrow, so users can tab forward
        //    through that screen's content.
        //  - Returning to the intro: the card that opened the screen the user
        //    just left, so focus comes back to where they were.
        focusIncomingScreen(targetScreen, outgoingScreen);

        // Update focusability synchronously so the tab order always matches the
        // visible screen, regardless of animation timing. Relying on the
        // fade-out's onComplete would leave a screen focusable if a rapid
        // navigation interrupts (and cancels) that tween.
        screens.forEach((screen) => {
            if (screen !== targetScreen) {
                screen.setAttribute('inert', '');
            }
        });

        // Reset z-index on all screens
        gsap.set(screens, { zIndex: 1 });

        // Promote the incoming screen to the in-flow (relative) screen so it
        // drives the container height, and drop the outgoing one to absolute so
        // it overlays during the crossfade without stretching the container.
        // This lets a taller screen (e.g. the browse screen) grow the tool on
        // small screens instead of overflowing onto the footer.
        outgoingScreen.style.position = 'absolute';
        targetScreen.style.position = 'relative';

        // Animate out the outgoing screen
        gsap.to(outgoingScreen, {
            opacity: 0,
            duration: 0.4,
            ease: 'power2.inOut',
            onComplete: () => {
                gsap.set(outgoingScreen, { pointerEvents: 'none' });
            },
        });

        // Animate in target screen
        gsap.to(targetScreen, {
            opacity: 1,
            duration: 0.4,
            ease: 'power2.inOut',
            pointerEvents: 'auto',
            zIndex: 10,
        });

        // Reset the incoming screen's scroll position to the top
        const targetInner = targetScreen.querySelector('.screen-inner');
        if (targetInner) {
            targetInner.scrollTop = 0;
        }

        currentScreen = targetScreen;
    }

    /**
     * Move keyboard focus to a sensible control on the screen being shown.
     *
     * Sub-screens expose a visible Back arrow, so focus goes there and the user
     * can tab forward through the screen. The intro screen has no visible Back
     * arrow; there, focus returns to the card that opened the screen the user
     * just came from (falling back to the first card).
     *
     * @param {HTMLElement} targetScreen   The screen becoming visible.
     * @param {HTMLElement} outgoingScreen The screen being hidden.
     */
    function focusIncomingScreen(targetScreen, outgoingScreen) {
        const backWrap = targetScreen.querySelector('.fullscreen-back');
        const backButton = targetScreen.querySelector('.fullscreen-back-btn');
        const backVisible = backWrap && window.getComputedStyle(backWrap).display !== 'none';

        if (backButton && backVisible) {
            backButton.focus({ preventScroll: true });
            return;
        }

        // Intro screen: return focus to the originating card, or the first card.
        const originCard = outgoingScreen && outgoingScreen.id
            ? targetScreen.querySelector(`a[href="#${window.CSS.escape(outgoingScreen.id)}"]`)
            : null;
        const focusTarget = originCard || targetScreen.querySelector('a.wp-block-pacecore-blocks-anchor-group');

        if (focusTarget) {
            focusTarget.focus({ preventScroll: true });
        }
    }
}
